import React, { useState, useMemo, useEffect } from 'react';
import {
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { AppCard } from '@/components/app-card/app-card';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { SvgHBars } from '@/components/ds/charts';

interface StackedChartDataItem {
  group_name: string;
  recommendation_status: string;
  total_monthly_savings: number;
}

interface OptimizationStatusChartsProps {
  display: 'all' | 'managed';
  defaultChartScope?: 'resource_owners' | 'systems' | 'departments';
  chartScope?: 'resource_owners' | 'systems' | 'departments';
  statusFilter?: 'all' | 'active';
}

function OptimizationStatusCharts({
  display,
  defaultChartScope = 'resource_owners',
  chartScope: controlledChartScope,
  statusFilter: controlledStatusFilter,
}: OptimizationStatusChartsProps) {
  const [internalChartScope, setInternalChartScope] = useState<
    'resource_owners' | 'systems' | 'departments'
  >(defaultChartScope);
  const [internalStatusFilter, setInternalStatusFilter] = useState<'all' | 'active'>('active');
  const [chartData, setChartData] = useState<StackedChartDataItem[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chartScope = controlledChartScope || internalChartScope;
  const statusFilter = controlledStatusFilter || internalStatusFilter;

  const handleScopeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newScope: 'resource_owners' | 'systems' | 'departments' | null,
  ) => {
    if (newScope !== null) {
      if (controlledChartScope) {
        return;
      } else {
        setInternalChartScope(newScope);
      }
    }
  };

  const handleStatusFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStatusFilter: 'all' | 'active' | null,
  ) => {
    if (newStatusFilter !== null) {
      if (controlledStatusFilter) {
        return;
      } else {
        setInternalStatusFilter(newStatusFilter);
      }
    }
  };

  // Fetch chart data when scope or status filter changes
  useEffect(() => {
    const fetchChartData = async () => {
      setDataLoaded(false);
      try {
        const data = await fetchWrapper.get(
          `${urls.DevOps.optimizationChartData}?scope=${chartScope}&statusFilter=${statusFilter}`
        );
        setChartData(data);
        setDataLoaded(true);
      } catch (error) {
        console.error('Error fetching optimization chart data:', error);
        setDataLoaded(true);
      }
    };

    fetchChartData();
  }, [chartScope, statusFilter]);

  const calculateStackedChartData = useMemo((): StackedChartDataItem[] => {
    // The backend already returns grouped data, so we just return it as-is
    return chartData;
  }, [chartData]);

  const getScopeDisplayName = (scope: string) => {
    switch (scope) {
      case 'resource_owners':
        return 'Resource Owners';
      case 'systems':
        return 'Systems';
      case 'departments':
        return 'Departments';
      default:
        return scope;
    }
  };

  // Single-series horizontal bars: total monthly savings per group (summed
  // across all statuses) to match the design's "Saving potential by …" chart.
  const savingsByGroup = useMemo(() => {
    const chartData = calculateStackedChartData;

    if (!chartData || chartData.length === 0) return [];

    const totals = new Map<string, number>();
    chartData.forEach((item) => {
      if (!item.group_name) return;
      totals.set(
        item.group_name,
        (totals.get(item.group_name) || 0) + (item.total_monthly_savings || 0),
      );
    });

    return [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([group_name, total]) => ({
        v: total,
        l: group_name,
        c: '#1C71E3',
      }));
  }, [calculateStackedChartData]);

  return (
      <AppCard
        className="card100"
        title={`Saving Potential (resource optimization, monthly) by ${getScopeDisplayName(chartScope)}`}
        icon={<BarChartOutlinedIcon color="primary" />}
        headerRight={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            {display === 'all' && (
                <>
                <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={handleStatusFilterChange}
                    size={isMobile ? 'small' : 'small'}
                    color="primary">
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="active">Active Only</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    value={chartScope}
                    exclusive
                    onChange={handleScopeChange}
                    size={isMobile ? 'small' : 'small'}
                    color="primary">
                    <ToggleButton value="resource_owners">
                    {isMobile ? 'Owners' : 'Resource owners'}
                    </ToggleButton>
                    <ToggleButton value="systems">Systems</ToggleButton>
                    <ToggleButton value="departments">
                    {isMobile ? 'Depts' : 'Departments'}
                    </ToggleButton>
                </ToggleButtonGroup>
                </>
            )}
            </div>
        }>
        <div
            style={{
            minHeight: '360px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            }}>
            {dataLoaded && savingsByGroup.length > 0 ? (
            <div style={{ width: '100%' }}>
              <SvgHBars
                data={savingsByGroup}
                fmt={formatCurrencyNoUnit}
                labelW={150}
              />
            </div>
            ) : dataLoaded ? (
            <Typography sx={{ color: '#8A94A6', fontSize: 13 }}>No optimization data available</Typography>
            ) : (
            <Typography sx={{ color: '#8A94A6', fontSize: 13 }}>Loading optimization data...</Typography>
            )}
        </div>
    </AppCard>
  );
}

export default OptimizationStatusCharts;
